import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, message, Popconfirm } from "antd";
import { getCustomerData } from "../../../API/User/customer";
import { SearchOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import eyeIcon from "../../../Assets/Icons/eye.svg";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import { deleteCustomer } from "../../../API/User/customer";
import "./style.css";

const CustomerList = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const successNotification = (msg) => {
    messageApi.success(msg);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const showDetails = (customer_id) => {
    navigate(`/view-customer-details/${customer_id}`);
  };
  const handleClick = () => {
    navigate("/add-customer-list");
  };

  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("email"),
    },
    {
      title: "No. Of Vehicles",
      key: "vehicle_count",
      sorter: (a, b) => (a?.vehicles?.length ?? 0) - (b?.vehicles?.length ?? 0),
      render: (_, record) => (
        <span className="vehicle-badge">
          {record?.vehicles?.length ?? 0} Vehicles
        </span>
      ),
    },
    {
      title: "Customer Phone Number",
      dataIndex: "customer_phone",
      key: "customer_phone",
      render: (text) => highlightText(`${text}`),
      ...getColumnSearchProps("customer_phone"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
      ],

      onFilter: (value, record) => {
        const statusText = record.status ? "Active" : "Inactive";
        return statusText === value;
      },

      render: (status) => {
        const statusText = status ? "Active" : "Inactive";

        return (
          <span className={`status-badge ${statusText.toLowerCase()}`}>
            {statusText}
          </span>
        );
      },
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "13px", alignItems: "center" }}>
          <a onClick={() => showDetails(record.id)}>
            <img src={eyeIcon} alt="view" />
          </a>
          <Popconfirm
            title="Are you sure to delete this?"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            okText="Yes"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.id)}
          >
            <a>
              <img src={delIcon} alt="Delete" />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCustomerData();

      if (Math.floor(response?.status / 100) === 2) {
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.customers || [];

        setCustomers(arr);
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (err) {
      messageApi.error("Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await deleteCustomer(id);

      if (Math.floor(res.status / 100) === 2) {
        successNotification("Customer deleted successfully");
        await fetchData();
      } else {
        errorNotification("Failed to delete customer");
      }
    } catch (err) {
      errorNotification("Error occurred while deleting customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-list">
      {contextHolder}
      <div className="button">
        <Button type="default" onClick={handleClick} className="add-btn">
          <img src={addIcon} alt="add user" />
          Create New Customer
        </Button>
      </div>
      <div className="table">
        <GenericTable
          dataSource={customers || []}
          columns={customerColumns}
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default CustomerList;
